/**
 * Hooks TanStack Query da Members API, um conjunto por recurso. Leituras usam
 * `useQuery`; escritas usam `useMutation` e invalidam as chaves afetadas. Tudo
 * passa pelo client ativo ({@link useBlogApi}), então mock e HTTP se comportam
 * igual — como no `lib/api` da plataforma.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { DEFAULT_SITE } from '../site/defaults';
import { useBlogApi } from './api-context';
import { blogKeys } from './query-keys';
import type {
  AdminSiteConfig,
  AdminSiteSettings,
  CommunityAccess,
  CourseCatalog,
  CourseDetail,
  CoursePurchase,
  CoursePurchaseInput,
  CourseResources,
  CourseSummary,
  Entitlement,
  HistoryItem,
  LessonContent,
  MemberProfile,
  MemberProfileInput,
  NotificationInput,
  NotificationsPage,
  PurchaseStatus,
  SiteConfig,
  SiteLink,
  SiteLinksInput,
  SiteNotification,
  SiteSettingsInput,
  TeamInput,
  TeamMember,
  Track,
} from './types';

/** Intervalo do polling de uma compra pendente (a baixa do Pix, a análise do cartão). */
export const PURCHASE_POLL_INTERVAL_MS = 4_000;
/**
 * Teto do polling: depois dele a tela para de reler sozinha uma compra ainda
 * pendente — uma aba esquecida não consulta o servidor por horas — e oferece a
 * releitura manual ({@link usePurchase}).
 */
export const PURCHASE_POLL_MAX_MS = 10 * 60 * 1000;

/** A configuração do site muda pelo admin, raramente: uma leitura a cada 5 min basta. */
export const SITE_STALE_MS = 5 * 60 * 1000;
/** O sino relê sozinho neste intervalo (o foco da janela não relê: o global desliga). */
export const NOTIFICATIONS_REFETCH_MS = 5 * 60 * 1000;

// ---- Site -----------------------------------------------------------------------

/**
 * A configuração pública do site. Enquanto carrega vale {@link DEFAULT_SITE};
 * em erro `data` fica vazio e o `SiteConfigProvider` (lib/site) cai nos defaults.
 */
export function useSite(): UseQueryResult<SiteConfig> {
  const api = useBlogApi();
  return useQuery({
    queryKey: blogKeys.site,
    queryFn: () => api.getSite(),
    placeholderData: DEFAULT_SITE,
    staleTime: SITE_STALE_MS,
  });
}

// ---- Catálogo público --------------------------------------------------------

export function useCourses(): UseQueryResult<CourseSummary[]> {
  const api = useBlogApi();
  return useQuery({ queryKey: blogKeys.courses.all, queryFn: () => api.listCourses() });
}

export function useTracks(): UseQueryResult<Track[]> {
  const api = useBlogApi();
  return useQuery({ queryKey: blogKeys.tracks, queryFn: () => api.listTracks() });
}

export interface CourseCatalogQuery {
  /** Definido assim que os cursos chegam; trilhas em erro valem lista vazia. */
  data: CourseCatalog | undefined;
  isPending: boolean;
  /** Só a falha dos cursos é erro: sem trilhas (rollback), a vitrine agrupa em "Outros cursos". */
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/** A vitrine composta: cursos + trilhas, cada um no seu cache. */
export function useCourseCatalog(): CourseCatalogQuery {
  const courses = useCourses();
  const tracks = useTracks();
  const { refetch: refetchCourses } = courses;
  const { refetch: refetchTracks } = tracks;
  const refetch = useCallback((): void => {
    void refetchCourses();
    void refetchTracks();
  }, [refetchCourses, refetchTracks]);
  const data = useMemo(
    () => (courses.data ? { courses: courses.data, tracks: tracks.data ?? [] } : undefined),
    [courses.data, tracks.data],
  );
  return {
    data,
    isPending: courses.isPending || tracks.isPending,
    isError: courses.isError,
    error: courses.error,
    refetch,
  };
}

export function useCourse(slug: string): UseQueryResult<CourseDetail> {
  const api = useBlogApi();
  return useQuery({ queryKey: blogKeys.courses.detail(slug), queryFn: () => api.getCourse(slug) });
}

// ---- Área logada ---------------------------------------------------------------

export function useMyEntitlements(enabled: boolean): UseQueryResult<Entitlement[]> {
  const api = useBlogApi();
  return useQuery({
    queryKey: blogKeys.entitlements,
    queryFn: () => api.listMyEntitlements(),
    // A rota é autenticada: sem sessão (ou enquanto ela resolve) a query nem sai.
    enabled,
  });
}

export function useMyPurchases(enabled: boolean): UseQueryResult<CoursePurchase[]> {
  const api = useBlogApi();
  return useQuery({
    queryKey: blogKeys.purchases.all,
    queryFn: () => api.listMyPurchases(),
    enabled,
  });
}

export type PurchaseQuery = UseQueryResult<CoursePurchase> & {
  /** O polling parou pelo teto ({@link PURCHASE_POLL_MAX_MS}) com a compra ainda pendente. */
  pollingStopped: boolean;
  /** Relê a compra agora e, se ela seguir pendente, acompanha por mais um teto. */
  resumePolling: () => void;
};

/**
 * Uma compra, com polling enquanto `pending` — é o acompanhamento do Pix (e do
 * cartão em análise): a baixa vira `confirmed` entre uma leitura e outra, e a
 * tela reage sozinha. O polling tem teto; quando a compra sai de `pending`, o
 * que depende dela (acessos, lista de compras, lições do curso) é invalidado
 * aqui, para toda tela que acompanha convergir ao servidor do mesmo jeito.
 */
export function usePurchase(purchaseId: string | null): PurchaseQuery {
  const api = useBlogApi();
  const qc = useQueryClient();
  // Cada ciclo de acompanhamento conta um teto; `resumePolling` abre outro.
  const [cycle, setCycle] = useState(0);
  const [capReached, setCapReached] = useState(false);
  const query = useQuery({
    queryKey: blogKeys.purchases.detail(purchaseId ?? ''),
    queryFn: () => api.getPurchase(purchaseId ?? ''),
    enabled: purchaseId !== null,
    refetchInterval: (q) =>
      q.state.data?.status === 'pending' && !capReached ? PURCHASE_POLL_INTERVAL_MS : false,
  });

  useEffect(() => {
    setCapReached(false);
    const timer = setTimeout(() => setCapReached(true), PURCHASE_POLL_MAX_MS);
    return () => clearTimeout(timer);
  }, [purchaseId, cycle]);

  const status = query.data?.status;
  const previousStatus = useRef<PurchaseStatus | undefined>(undefined);
  useEffect(() => {
    if (previousStatus.current === 'pending' && status !== undefined && status !== 'pending') {
      void qc.invalidateQueries({ queryKey: blogKeys.entitlements });
      void qc.invalidateQueries({ queryKey: blogKeys.purchases.all });
      // A compra confirmada abre a comunidade e os materiais do curso.
      void qc.invalidateQueries({ queryKey: blogKeys.community });
      void qc.invalidateQueries({ queryKey: blogKeys.resources });
      if (query.data) {
        void qc.invalidateQueries({
          queryKey: blogKeys.courses.lessonContentAll(query.data.courseSlug),
        });
      }
    }
    previousStatus.current = status;
  }, [status, query.data, qc]);

  const { refetch } = query;
  const resumePolling = useCallback((): void => {
    setCycle((n) => n + 1);
    void refetch();
  }, [refetch]);

  return { ...query, pollingStopped: capReached && status === 'pending', resumePolling };
}

/**
 * O conteúdo de uma lição — a query é a prova de entitlement do §4.3: amostras
 * abrem para qualquer um; as demais respondem 401/403 e a UI as trata como
 * trancadas. `enabled` deixa a página decidir QUANDO pedir (lição aberta).
 */
export function useLessonContent(
  courseSlug: string,
  lessonId: string | null,
): UseQueryResult<LessonContent> {
  const api = useBlogApi();
  return useQuery({
    queryKey: blogKeys.courses.lessonContent(courseSlug, lessonId ?? ''),
    queryFn: () => api.getLessonContent(courseSlug, lessonId ?? ''),
    enabled: lessonId !== null,
    // Um 403 (sem entitlement) não se resolve sozinho; o retry padrão só esperaria.
    retry: false,
  });
}

export function useMyProfile(enabled: boolean): UseQueryResult<MemberProfile> {
  const api = useBlogApi();
  return useQuery({ queryKey: blogKeys.profile, queryFn: () => api.getMyProfile(), enabled });
}

export function useMyHistory(enabled: boolean): UseQueryResult<HistoryItem[]> {
  const api = useBlogApi();
  // O servidor grava a aula aberta por conta própria: cada visita relê, para o
  // "Continuar" e o progresso não ficarem presos no cache de 30 s.
  return useQuery({
    queryKey: blogKeys.history,
    queryFn: () => api.listMyHistory(),
    enabled,
    staleTime: 0,
  });
}

export function useMyResources(enabled: boolean): UseQueryResult<CourseResources[]> {
  const api = useBlogApi();
  return useQuery({ queryKey: blogKeys.resources, queryFn: () => api.listMyResources(), enabled });
}

export function useMyCommunity(enabled: boolean): UseQueryResult<CommunityAccess> {
  const api = useBlogApi();
  return useQuery({ queryKey: blogKeys.community, queryFn: () => api.getMyCommunity(), enabled });
}

/** O sino: relê a cada {@link NOTIFICATIONS_REFETCH_MS} enquanto a sessão existe. */
export function useMyNotifications(enabled: boolean): UseQueryResult<NotificationsPage> {
  const api = useBlogApi();
  return useQuery({
    queryKey: blogKeys.notifications,
    queryFn: () => api.listMyNotifications(),
    enabled,
    refetchInterval: enabled ? NOTIFICATIONS_REFETCH_MS : false,
  });
}

// ---- Administração (leituras) ----------------------------------------------------

export function useAdminSite(): UseQueryResult<AdminSiteConfig> {
  const api = useBlogApi();
  return useQuery({ queryKey: blogKeys.adminSite, queryFn: () => api.adminGetSite() });
}

export function useAdminNotifications(): UseQueryResult<SiteNotification[]> {
  const api = useBlogApi();
  return useQuery({
    queryKey: blogKeys.adminNotifications,
    queryFn: () => api.adminListNotifications(),
  });
}

// ---- Escritas --------------------------------------------------------------------

export function useEnrollFreeCourse(): UseMutationResult<Entitlement, Error, string> {
  const api = useBlogApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => api.enrollFreeCourse(slug),
    onSuccess: (entitlement) => {
      void qc.invalidateQueries({ queryKey: blogKeys.entitlements });
      // O sumário passa a mostrar a lição aberta; o conteúdo dela destrava.
      void qc.invalidateQueries({ queryKey: blogKeys.courses.detail(entitlement.courseSlug) });
      // Os materiais do curso entram em "Recursos".
      void qc.invalidateQueries({ queryKey: blogKeys.resources });
    },
  });
}

export function useStartCoursePurchase(): UseMutationResult<
  CoursePurchase,
  Error,
  { slug: string; input: CoursePurchaseInput }
> {
  const api = useBlogApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, input }) => api.startCoursePurchase(slug, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: blogKeys.purchases.all });
      void qc.invalidateQueries({ queryKey: blogKeys.entitlements });
      // O cartão confirma na hora: comunidade e materiais já valem.
      void qc.invalidateQueries({ queryKey: blogKeys.community });
      void qc.invalidateQueries({ queryKey: blogKeys.resources });
    },
  });
}

export function useCancelPurchase(): UseMutationResult<CoursePurchase, Error, string> {
  const api = useBlogApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (purchaseId: string) => api.cancelPurchase(purchaseId),
    onSuccess: (purchase) => {
      void qc.invalidateQueries({ queryKey: blogKeys.purchases.all });
      void qc.invalidateQueries({ queryKey: blogKeys.purchases.detail(purchase.id) });
      // O estorno revogou o entitlement: a conta e o curso refletem na hora.
      void qc.invalidateQueries({ queryKey: blogKeys.entitlements });
      void qc.invalidateQueries({ queryKey: blogKeys.courses.lessonContentAll(purchase.courseSlug) });
      // A comunidade e os materiais vêm com a compra: o estorno pode tirá-los.
      void qc.invalidateQueries({ queryKey: blogKeys.community });
      void qc.invalidateQueries({ queryKey: blogKeys.resources });
    },
  });
}

export function useUpdateMyProfile(): UseMutationResult<MemberProfile, Error, MemberProfileInput> {
  const api = useBlogApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MemberProfileInput) => api.updateMyProfile(input),
    onSuccess: (profile) => qc.setQueryData(blogKeys.profile, profile),
  });
}

/** Ao abrir o sino: o ponto some na hora (a resposta já vem com `unseenCount` 0). */
export function useMarkNotificationsSeen(): UseMutationResult<NotificationsPage, Error, void> {
  const api = useBlogApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.markNotificationsSeen(),
    onSuccess: (page) => qc.setQueryData(blogKeys.notifications, page),
  });
}

/** As escritas do site mudam o /admin e o chrome público de uma vez. */
function useSiteWrite<TInput, TResult>(
  write: (input: TInput) => Promise<TResult>,
): UseMutationResult<TResult, Error, TInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: blogKeys.adminSite });
      void qc.invalidateQueries({ queryKey: blogKeys.site });
    },
  });
}

export function useAdminSaveSiteSettings(): UseMutationResult<
  AdminSiteSettings,
  Error,
  SiteSettingsInput
> {
  const api = useBlogApi();
  return useSiteWrite((input: SiteSettingsInput) => api.adminSaveSiteSettings(input));
}

export function useAdminSaveSiteLinks(): UseMutationResult<SiteLink[], Error, SiteLinksInput> {
  const api = useBlogApi();
  return useSiteWrite((input: SiteLinksInput) => api.adminSaveSiteLinks(input));
}

export function useAdminSaveTeam(): UseMutationResult<TeamMember[], Error, TeamInput> {
  const api = useBlogApi();
  return useSiteWrite((input: TeamInput) => api.adminSaveTeam(input));
}

/** As escritas de avisos mudam a lista do /admin e o sino de quem está logado. */
function useNotificationWrite<TInput, TResult>(
  write: (input: TInput) => Promise<TResult>,
): UseMutationResult<TResult, Error, TInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: blogKeys.adminNotifications });
      void qc.invalidateQueries({ queryKey: blogKeys.notifications });
    },
  });
}

export function useAdminSaveNotification(): UseMutationResult<
  SiteNotification,
  Error,
  NotificationInput
> {
  const api = useBlogApi();
  return useNotificationWrite((input: NotificationInput) => api.adminSaveNotification(input));
}

export function useAdminDeleteNotification(): UseMutationResult<{ deleted: true }, Error, string> {
  const api = useBlogApi();
  return useNotificationWrite((notificationId: string) =>
    api.adminDeleteNotification(notificationId),
  );
}
